import type { HumanName } from "@medplum/fhirtypes";
import { toHumanName } from "@/lib/medplum/utils";
import { normalizeForSearch, normalizeNameParts } from "@/lib/utils/string";

interface BuildHumanNameParams {
  name?: string | null;
  use: HumanName["use"];
}

interface NameLookupParams {
  names?: HumanName[];
}

interface MergeHumanNamesParams {
  existingNames?: HumanName[];
  legalName?: string | null;
  preferredName?: string | null;
}

interface MergeLegalHumanNamePartsParams {
  existingNames?: HumanName[];
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}

interface BuildLegalHumanNameFromPartsParams {
  existingLegalName?: HumanName;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}

/**
 * Shared HumanName helpers for member identity and demographic updates.
 */
export function buildHumanName({ name, use }: BuildHumanNameParams): HumanName | undefined {
  const baseName = toHumanName(name)?.[0];
  if (!baseName) {
    return undefined;
  }

  return { ...baseName, use };
}

/**
 * Whether a stored name is the person named by `firstName` and `lastName`. Case- and
 * accent-insensitive. The first name matches the first given name or all given names joined, so
 * "Mary Ann" matches `given: ["Mary", "Ann"]` and "Mary" matches it too. False when either part
 * is blank.
 *
 * The migration upload stops on this match (`Member.searchByNameAndBirthDateInPractice`) and the
 * dry run's in-export check predicts it, so both call this one rule.
 */
export function isSameFirstAndLastName(
  name: HumanName,
  { firstName, lastName }: { firstName?: string; lastName?: string },
): boolean {
  const given = normalizeForSearch(firstName);
  const family = normalizeForSearch(lastName);
  if (!given || !family) return false;
  const givenNames = [name.given?.[0], name.given?.join(" ")].map(normalizeForSearch);
  return givenNames.includes(given) && normalizeForSearch(name.family) === family;
}

/**
 * Returns the preferred legal name entry, falling back to an unnamed entry.
 */
function getLegalHumanName({ names }: NameLookupParams): HumanName | undefined {
  const official = names?.find((name) => name.use === "official");
  if (official) {
    return official;
  }

  return names?.find((name) => !name.use);
}

/**
 * Returns the preferred (usual) name entry from a list of HumanName values.
 */
export function getPreferredHumanName({ names }: NameLookupParams): HumanName | undefined {
  return names?.find((name) => name.use === "usual");
}

/**
 * Returns the best available name entry for display.
 */
export function getPrimaryHumanName({ names }: NameLookupParams): HumanName | undefined {
  return getLegalHumanName({ names }) ?? names?.[0];
}

/**
 * Merges legal and preferred name updates into existing names while preserving other entries.
 */
export function mergeHumanNames({
  existingNames,
  legalName,
  preferredName,
}: MergeHumanNamesParams): HumanName[] | undefined {
  const existing = existingNames ?? [];
  const existingLegalName = getLegalHumanName({ names: existing });
  const existingPreferredName = getPreferredHumanName({ names: existing });
  const otherNames = existing.filter(
    (name) => name !== existingLegalName && name !== existingPreferredName,
  );

  const nextLegalName =
    typeof legalName === "undefined"
      ? withOfficialUse(existingLegalName)
      : buildHumanName({ name: legalName, use: "official" });
  const nextPreferredName =
    typeof preferredName === "undefined"
      ? existingPreferredName
      : buildHumanName({ name: preferredName, use: "usual" });
  const merged = [nextLegalName, nextPreferredName, ...otherNames].filter(Boolean) as HumanName[];

  return merged.length > 0 ? merged : undefined;
}

/**
 * Merges split legal-name fields into the legal HumanName entry while preserving preferred names.
 * `undefined` preserves the current value; `null` clears the part explicitly.
 */
export function mergeLegalHumanNameParts({
  existingNames,
  firstName,
  middleName,
  lastName,
}: MergeLegalHumanNamePartsParams): HumanName[] | undefined {
  if (firstName === undefined && middleName === undefined && lastName === undefined) {
    return undefined;
  }

  const existing = existingNames ?? [];
  const existingLegalName = getLegalHumanName({ names: existing });
  const existingPreferredName = getPreferredHumanName({ names: existing });
  const otherNames = existing.filter(
    (name) => name !== existingLegalName && name !== existingPreferredName,
  );
  const nextLegalName = buildLegalHumanNameFromParts({
    existingLegalName,
    firstName,
    middleName,
    lastName,
  });
  const merged = [nextLegalName, existingPreferredName, ...otherNames].filter(
    Boolean,
  ) as HumanName[];

  return merged.length > 0 ? merged : undefined;
}

/**
 * Builds the next legal HumanName entry from split first/middle/last fields.
 */
function buildLegalHumanNameFromParts({
  existingLegalName,
  firstName,
  middleName,
  lastName,
}: BuildLegalHumanNameFromPartsParams): HumanName | undefined {
  const existingGivenNames = existingLegalName?.given ?? [];
  const preservedMiddleNames = getPreservedMiddleNames({
    existingMiddleNames: existingGivenNames.slice(1),
    firstName,
    middleName,
  });
  const nextFirstName = firstName === undefined ? existingGivenNames[0] : (firstName ?? undefined);
  const nextGivenNames = [nextFirstName, ...preservedMiddleNames].filter((name): name is string =>
    Boolean(name),
  );
  const nextFamilyName =
    lastName === undefined ? existingLegalName?.family : (lastName ?? undefined);

  if (nextGivenNames.length === 0 && !nextFamilyName) {
    return undefined;
  }

  return {
    ...existingLegalName,
    text: undefined,
    given: nextGivenNames.length > 0 ? nextGivenNames : undefined,
    family: nextFamilyName,
    use: "official",
  };
}

function caseFoldNamePart(namePart: string): string {
  return namePart.toLowerCase();
}

function getCanonicalNameValue(name?: string | null): string | undefined {
  const normalizedParts = normalizeNameParts(name ?? undefined);
  return normalizedParts ? normalizedParts.map(caseFoldNamePart).join(" ") : undefined;
}

/**
 * Preserves existing middle names unless Hint already included them in the first-name field.
 * Explicit first-name clears drop preserved middles so they do not become the new given[0].
 */
function getPreservedMiddleNames({
  existingMiddleNames,
  firstName,
  middleName,
}: {
  existingMiddleNames: string[];
  firstName?: string | null;
  middleName?: string | null;
}): string[] {
  if (middleName !== undefined) {
    return middleName ? [middleName] : [];
  }

  if (firstName === undefined) {
    return existingMiddleNames;
  }

  if (firstName === null) {
    return [];
  }

  const hintFirstNameParts = new Set((normalizeNameParts(firstName) ?? []).map(caseFoldNamePart));
  return existingMiddleNames.filter((name) => {
    const canonicalName = getCanonicalNameValue(name);
    return !canonicalName || !hintFirstNameParts.has(canonicalName);
  });
}

function withOfficialUse(name: HumanName | undefined): HumanName | undefined {
  return name ? { ...name, use: "official" } : undefined;
}
